import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { Dialog, DialogPanel, Transition, TransitionChild } from '@headlessui/react';
import { Link } from 'react-router-dom';
import {
  CheckCircleIcon,
  SparklesIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import { GoogleSignInButton } from '../GoogleSignInButton';
import { useAppDispatch } from '../../../hooks/useAppDispatch';
import { useAppSelector } from '../../../hooks/useAppSelector';
import {
  authenticateWithGoogle,
  getCurrentUser,
  loginUser,
  selectIsAuthenticated,
} from '../../../store/features/auth';
import { tokenStore } from '../../../services/tokenStore';
import { getAuthPromptConfig } from './authPromptMoments';
import {
  buildAuthPromptAnalyticsBase,
  trackAuthPromptDismiss,
  trackAuthPromptShown,
  trackAuthPromptSuccess,
} from './authPromptAnalytics';
import type { AuthPromptOpenOptions, AuthPromptUserStatus } from './authPrompt.types';

type AuthPromptModalProps = {
  isOpen: boolean;
  options: AuthPromptOpenOptions | null;
  userStatus: AuthPromptUserStatus;
  onClose: () => void;
};

function showAuthSuccessToast(isNewUser: boolean): void {
  if (isNewUser) {
    toast.success('Account created. Welcome to Qumashi!');
  } else {
    toast.success('Welcome back!');
  }
}

async function runOnSuccess(
  onSuccess?: () => void | Promise<void>,
): Promise<void> {
  if (!onSuccess) return;
  await onSuccess();
}

export function AuthPromptModal({
  isOpen,
  options,
  userStatus,
  onClose,
}: AuthPromptModalProps) {
  const dispatch = useAppDispatch();
  const isAuthenticated = useAppSelector(selectIsAuthenticated);
  const twoFactorPending = useAppSelector((state) => state.auth.twoFactorPending);
  const awaitingTwoFactorRef = useRef(false);
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [emailLoading, setEmailLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [error, setError] = useState('');

  const moment = options?.moment ?? 'account_access';
  const config = getAuthPromptConfig(moment, options?.preferSignIn);
  const prefilledEmail = options?.email?.trim() ?? '';
  const needsPasswordSetup = Boolean(options?.needsPasswordSetup);
  const isCheckoutReturning = moment === 'checkout' && Boolean(prefilledEmail);
  const sourcePage = options?.sourcePage ?? '/';

  const analyticsBase = useMemo(
    () => buildAuthPromptAnalyticsBase(moment, sourcePage, userStatus),
    [moment, sourcePage, userStatus],
  );

  const busy = emailLoading || googleLoading;

  useEffect(() => {
    if (!isOpen) {
      setShowEmailForm(false);
      setPassword('');
      setError('');
      setEmail(prefilledEmail);
      awaitingTwoFactorRef.current = false;
      return;
    }
    setEmail(prefilledEmail);
    trackAuthPromptShown(analyticsBase);
    if (isCheckoutReturning) {
      setShowEmailForm(true);
    }
  }, [isOpen, prefilledEmail, isCheckoutReturning, analyticsBase]);

  useEffect(() => {
    if (
      !isOpen ||
      !awaitingTwoFactorRef.current ||
      twoFactorPending ||
      !isAuthenticated
    ) {
      return;
    }

    awaitingTwoFactorRef.current = false;
    void finishSuccess('google', false);
  }, [isOpen, isAuthenticated, twoFactorPending]);

  const finishSuccess = async (authMethod: 'google' | 'email', isNewUser?: boolean) => {
    if (authMethod === 'google') {
      showAuthSuccessToast(Boolean(isNewUser));
    } else {
      toast.success('Welcome back!');
    }
    trackAuthPromptSuccess({ ...analyticsBase, authMethod });
    try {
      await runOnSuccess(options?.onSuccess);
    } catch {
      toast.error('Signed in, but we could not finish your action. Please try again.');
    }
    onClose();
  };

  const handleClose = () => {
    if (busy) return;
    trackAuthPromptDismiss(analyticsBase);
    options?.onDismiss?.();
    onClose();
  };

  const handleGuestContinue = () => {
    trackAuthPromptDismiss({ ...analyticsBase, authMethod: 'guest' });
    options?.onDismiss?.();
    onClose();
  };

  const handleGoogle = async () => {
    setGoogleLoading(true);
    setError('');
    try {
      const result = await dispatch(
        authenticateWithGoogle({
          intent: config.googleIntent,
          surface: config.analyticsSurface,
          suppressSuccessToast: true,
        }),
      );
      if (authenticateWithGoogle.fulfilled.match(result)) {
        if (result.payload.requiresTwoFactor) {
          awaitingTwoFactorRef.current = true;
          return;
        }
        await finishSuccess('google', result.payload.isNewUser);
      }
    } finally {
      setGoogleLoading(false);
    }
  };

  const handleEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const emailValue = (prefilledEmail || email).trim();
    if (!emailValue || !password) {
      setError('Email and password are required');
      return;
    }

    setEmailLoading(true);
    setError('');
    try {
      const result = await dispatch(
        loginUser({ email: emailValue, password, silent: true }),
      ).unwrap();
      if (result.requiresTwoFactor) {
        setError('Two-factor authentication is required. Please sign in from the login page.');
        return;
      }
      await dispatch(getCurrentUser()).unwrap();
      await finishSuccess('email', false);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Invalid email or password';
      setError(message);
    } finally {
      setEmailLoading(false);
    }
  };

  const expandEmailForm = () => {
    setShowEmailForm(true);
  };

  return (
    <Transition show={isOpen && Boolean(options)} as={Fragment}>
      <Dialog className="relative z-[200]" onClose={handleClose}>
        <TransitionChild
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-slate-900/55 backdrop-blur-[2px]" aria-hidden />
        </TransitionChild>

        <div className="fixed inset-0 flex items-end justify-center p-0 sm:items-center sm:p-4">
          <TransitionChild
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0 translate-y-6 sm:translate-y-2 sm:scale-[0.97]"
            enterTo="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100 translate-y-0 sm:scale-100"
            leaveTo="opacity-0 translate-y-6 sm:translate-y-2 sm:scale-[0.97]"
          >
            <DialogPanel
              className="relative w-full max-w-md overflow-hidden rounded-t-3xl bg-white shadow-2xl ring-1 ring-slate-900/5 sm:rounded-3xl"
              data-testid="auth-prompt-modal"
            >
              <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-[#3B3B98] via-purple-500 to-fuchsia-500" />

              <button
                type="button"
                onClick={handleClose}
                disabled={busy}
                className="absolute right-3 top-3 z-10 rounded-full p-2 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600 disabled:opacity-50"
                aria-label="Close"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>

              <div className="max-h-[90vh] overflow-y-auto px-5 pb-5 pt-6 sm:px-7 sm:pb-7 sm:pt-8">
                <p className="text-xs font-bold uppercase tracking-[0.14em] text-purple-600">
                  {config.eyebrow}
                </p>
                <h2 className="mt-2 pr-8 text-xl font-bold tracking-tight text-slate-900 sm:text-2xl">
                  {config.title}
                </h2>
                <p className="mt-2 text-sm leading-relaxed text-slate-600">{config.description}</p>

                {needsPasswordSetup ? (
                  <div className="mt-4 rounded-2xl border border-amber-200 bg-amber-50/90 p-3.5 text-sm text-amber-950">
                    <p className="font-medium">Finish setting up your account</p>
                    <p className="mt-1 text-amber-900/90">
                      We emailed you a secure link to create a password. You can also set one now if you&apos;re on this device.
                    </p>
                    <div className="mt-3 flex flex-wrap gap-3">
                      <Link
                        to={`/forgot-password?email=${encodeURIComponent(prefilledEmail || email)}`}
                        onClick={handleClose}
                        className="text-sm font-semibold text-amber-900 underline-offset-2 hover:underline"
                      >
                        Resend setup email
                      </Link>
                      {tokenStore.getAccessToken() ? (
                        <Link
                          to="/account/set-password"
                          onClick={handleClose}
                          className="text-sm font-semibold text-amber-900 underline-offset-2 hover:underline"
                        >
                          Set password now
                        </Link>
                      ) : null}
                    </div>
                  </div>
                ) : null}

                <ul className="mt-4 space-y-2 rounded-2xl border border-purple-100 bg-purple-50/70 p-3.5">
                  {config.benefits.map((benefit) => (
                    <li key={benefit} className="flex items-start gap-2 text-sm text-purple-900">
                      <CheckCircleIcon className="mt-0.5 h-4 w-4 shrink-0 text-purple-600" />
                      <span>{benefit}</span>
                    </li>
                  ))}
                </ul>

                <div className="mt-5 space-y-3">
                  <GoogleSignInButton
                    onClick={() => void handleGoogle()}
                    disabled={busy}
                    isLoading={googleLoading}
                    label={config.googleLabel}
                  />

                  {!showEmailForm ? (
                    <button
                      type="button"
                      onClick={expandEmailForm}
                      disabled={busy}
                      className="flex h-11 w-full items-center justify-center gap-2 rounded-xl border border-slate-200 bg-white text-sm font-semibold text-slate-700 shadow-sm transition hover:border-purple-200 hover:bg-purple-50/50 disabled:opacity-50"
                    >
                      <SparklesIcon className="h-4 w-4 text-purple-500" />
                      {config.emailCta}
                    </button>
                  ) : (
                    <form
                      onSubmit={(e) => void handleEmailSubmit(e)}
                      className="space-y-3 rounded-2xl border border-slate-200 bg-slate-50/60 p-4"
                    >
                      {!prefilledEmail ? (
                        <div>
                          <label htmlFor="auth-prompt-email" className="block text-sm font-medium text-slate-700">
                            Email
                          </label>
                          <input
                            id="auth-prompt-email"
                            type="email"
                            autoComplete="email"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm focus:border-purple-400 focus:outline-none focus:ring-2 focus:ring-purple-100"
                            disabled={busy}
                            required
                          />
                        </div>
                      ) : (
                        <div>
                          <label className="block text-sm font-medium text-slate-700">Email</label>
                          <input
                            type="email"
                            value={prefilledEmail}
                            readOnly
                            className="mt-1 w-full rounded-xl border border-slate-200 bg-slate-100 px-3.5 py-2.5 text-sm text-slate-700"
                          />
                        </div>
                      )}

                      <div>
                        <label htmlFor="auth-prompt-password" className="block text-sm font-medium text-slate-700">
                          Password
                        </label>
                        <input
                          id="auth-prompt-password"
                          type="password"
                          autoComplete={config.googleIntent === 'register' ? 'new-password' : 'current-password'}
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          autoFocus={isCheckoutReturning}
                          className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm focus:border-purple-400 focus:outline-none focus:ring-2 focus:ring-purple-100"
                          disabled={busy}
                          required
                        />
                      </div>

                      {error ? (
                        <p className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-800" role="alert">
                          {error}
                        </p>
                      ) : null}

                      <button
                        type="submit"
                        disabled={busy}
                        className="flex h-11 w-full items-center justify-center rounded-xl bg-gradient-to-r from-[#3B3B98] to-purple-600 text-sm font-semibold text-white shadow-sm transition hover:shadow-md disabled:opacity-50"
                      >
                        {emailLoading
                          ? 'Signing in…'
                          : needsPasswordSetup
                            ? 'Try sign in'
                            : isCheckoutReturning
                              ? 'Sign in & continue'
                              : 'Sign in'}
                      </button>

                      {!isCheckoutReturning ? (
                        <button
                          type="button"
                          onClick={() => setShowEmailForm(false)}
                          disabled={busy}
                          className="w-full text-center text-xs text-slate-500 hover:text-slate-700"
                        >
                          ← Other sign-in options
                        </button>
                      ) : null}
                    </form>
                  )}
                </div>

                {config.showRegisterLink && !isCheckoutReturning ? (
                  <p className="mt-4 text-center text-sm text-slate-600">
                    New to Qumashi?{' '}
                    <Link
                      to="/register"
                      onClick={handleClose}
                      className="font-semibold text-purple-600 hover:text-purple-700"
                    >
                      Create an account
                    </Link>
                    {' · '}
                    <span className="text-slate-500">or use Google — we&apos;ll set one up for you</span>
                  </p>
                ) : null}

                {config.allowGuest ? (
                  <div className="mt-4 border-t border-slate-100 pt-4 text-center">
                    <button
                      type="button"
                      onClick={handleGuestContinue}
                      disabled={busy}
                      className="text-sm font-medium text-slate-500 transition hover:text-slate-800 disabled:opacity-50"
                    >
                      {config.guestContinueLabel}
                    </button>
                  </div>
                ) : null}

                <p className="mt-4 text-center text-[11px] leading-relaxed text-slate-400">
                  {config.trustLine}
                </p>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </Dialog>
    </Transition>
  );
}
